import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Button,
  IconButton,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { DataService } from 'src/services/data-service';

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  floor: number;
  areaM2: number;
  propertyId: string;
  property: any;
  unitFeeConfigs: any[];
  appliances: any[];
  active: boolean;
}

interface Lease {
  id: string;
  createdDate: string;
  startDate: string;
  endDate: string;
  status: string;
  refNumber: string;
  leaseType: string;
  leaseTypeId: string;
  currency: string;
  currencyId: string;
  unit: Unit;
  tenant: any;
  fees: Array<{
    id: string;
    value: number;
    type: string;
    currency: string;
    currencyId: string;
    billingCycle: string;
    notes: string | null;
    active: boolean;
  }>;
  active: boolean;
}

interface Installment {
  id: string;
  issuedAt: string;
  dueDate: string;
  status: string;
  total: number;
  currency: string | null;
  fee: any;
  lease: {
    id: string;
    createdDate: string;
    startDate: string;
    endDate: string;
    status: string;
    refNumber: string;
    leaseTypeId: string;
    leaseType: string | null;
    currencyId: string;
    currency: string | null;
    unit: any;
    tenant: any;
    fees: any[];
    active: boolean;
  };
  active: boolean;
}

interface Tenant {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string | null;
  identificationType: string;
  identificationTypeId: string;
  active: boolean;
}

// Status mapping for string-based status
const LeaseStatus: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }> = {
  'Draft': { label: 'Draft', color: 'warning' },
  'Active': { label: 'Active', color: 'success' },
  'Expired': { label: 'Expired', color: 'error' },
  'Terminated': { label: 'Terminated', color: 'default' }
};

const InstallmentStatus: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }> = {
  'New': { label: 'New', color: 'info' },
  'Paid': { label: 'Paid', color: 'success' },
  'Overdue': { label: 'Overdue', color: 'error' },
  'Cancelled': { label: 'Cancelled', color: 'default' }
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8]
  }
}));

const LeaseHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`
}));

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[4]
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1.5 }}>{children}</Box>}
    </div>
  );
}

const TenantDetailsPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [loading, setLoading] = useState(true);
  const [leasesLoading, setLeasesLoading] = useState(false);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetails();
      fetchTenantLeases();
      fetchTenantInstallments();
    }
  }, [tenantId]);

  const fetchTenantDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/Tenant/get/${tenantId}`);

      if (response.ok) {
        const responseData = await response.json();
        let tenantData: Tenant;
        if (responseData.success && responseData.data) {
          tenantData = responseData.data;
        } else if (responseData.id) {
          tenantData = responseData;
        } else {
          throw new Error('Invalid response structure');
        }
        setTenant(tenantData);
      } else {
        throw new Error(`Failed to fetch tenant: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load tenant details. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantLeases = async () => {
    setLeasesLoading(true);
    try {
      const response = await DataService.get(`api/Lease/tenant/${tenantId}`);

      if (response.ok) {
        const responseData = await response.json();
        let leasesList: Lease[] = [];

        if (responseData.success && responseData.data) {
          leasesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          leasesList = responseData;
        }

        setLeases(leasesList);
      } else {
        throw new Error('Failed to fetch tenant leases');
      }
    } catch (error) {
      console.error('Error fetching tenant leases:', error);
      setLeases([]);
    } finally {
      setLeasesLoading(false);
    }
  };

  const fetchTenantInstallments = async () => {
    setInstallmentsLoading(true);
    try {
      const response = await DataService.get(`api/Installment/tenant/${tenantId}`);

      if (response.ok) {
        const responseData = await response.json();
        let installmentsList: Installment[] = [];

        if (responseData.success && responseData.data) {
          installmentsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          installmentsList = responseData;
        }

        setInstallments(installmentsList);
      } else {
        throw new Error('Failed to fetch tenant installments');
      }
    } catch (error) {
      console.error('Error fetching tenant installments:', error);
      setInstallments([]);
    } finally {
      setInstallmentsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusInfo = (status: string) => {
    return LeaseStatus[status] || { label: status || 'Unknown', color: 'default' as const };
  };

  const getInstallmentStatusInfo = (status: string) => {
    return InstallmentStatus[status] || { label: status || 'Unknown', color: 'default' as const };
  };

  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Group installments by lease
  const groupedInstallments = installments.reduce((acc, installment) => {
    const leaseId = installment.lease.id;
    if (!acc[leaseId]) {
      acc[leaseId] = {
        lease: installment.lease,
        installments: []
      };
    }
    acc[leaseId].installments.push(installment);
    return acc;
  }, {} as Record<string, { lease: any; installments: Installment[] }>);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !tenant) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Tenant not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenants')}
        >
          Back to Tenants
        </Button>
      </Container>
    );
  }

  const initials = getInitials(tenant.fullName);

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      {/* Page Header */}
      <PageHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/tenants')}
              size="small"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                fontSize: '1.5rem',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ mb: 0.5 }}>
                {tenant.fullName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={tenant.active ? <CheckCircleIcon /> : <CancelIcon />}
                  label={tenant.active ? 'Active' : 'Inactive'}
                  color={tenant.active ? 'success' : 'default'}
                  sx={{
                    bgcolor: tenant.active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  ID: {tenant.id}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Edit
          </Button>
        </Box>
      </PageHeader>

      {/* Tenant Information Section */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 'bold' }}>
                <EmailIcon color="primary" />
                Contact Information
              </Typography>

              {/* Email Addresses */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Email Addresses
                </Typography>
                {tenant.emails.length > 0 ? (
                  <List dense>
                    {tenant.emails.map((email, index) => (
                      <ListItem key={index} sx={{ pl: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <EmailIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={email}
                          primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                    No email addresses provided
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Phone Numbers */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Phone Numbers
                </Typography>
                {tenant.phoneNumbers.length > 0 ? (
                  <List dense>
                    {tenant.phoneNumbers.map((phone, index) => (
                      <ListItem key={index} sx={{ pl: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PhoneIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={phone}
                          primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                    No phone numbers provided
                  </Typography>
                )}
              </Box>
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonIcon color="primary" />
                Personal Information
              </Typography>

              {/* Nationality */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Nationality
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                  <PublicIcon fontSize="small" color="action" />
                  <Typography variant="body1" fontWeight={500}>
                    {tenant.nationality || (
                      <span style={{ fontStyle: 'italic', color: 'gray' }}>
                        Not specified
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Identification Type */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Identification Type
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                  <BadgeIcon fontSize="small" color="action" />
                  <Typography variant="body1" fontWeight={500}>
                    {tenant.identificationType || (
                      <span style={{ fontStyle: 'italic', color: 'gray' }}>
                        Not specified
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Box>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              bgcolor: 'primary.main',
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 'bold',
                fontSize: '1rem',
                py: 2
              },
              '& .Mui-selected': {
                color: 'white !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'white',
                height: 3
              }
            }}
          >
            <Tab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label={`Leases (${leases.length})`}
            />
            <Tab
              icon={<ReceiptIcon />}
              iconPosition="start"
              label={`Installments (${installments.length})`}
            />
          </Tabs>

          {/* Leases Tab */}
          <TabPanel value={tabValue} index={0}>
            {leasesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress size={60} />
              </Box>
            ) : leases.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                <DescriptionIcon sx={{ fontSize: 60, color: 'grey.300', mb: 1 }} />
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  No leases found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This tenant doesn't have any leases yet.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={1.5}>
                {leases.map((lease) => {
                  const statusInfo = getStatusInfo(lease.status);
                  const daysRemaining = getDaysRemaining(lease.endDate);

                  return (
                    <Grid item xs={12} sm={6} lg={4} key={lease.id}>
                      <StyledCard>
                        {/* Lease Header */}
                        <LeaseHeader>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="h6" fontWeight="bold">
                              Lease #{lease.id.substring(0, 8)}
                            </Typography>
                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                          {lease.refNumber && (
                            <Typography variant="body2">
                              Ref: {lease.refNumber}
                            </Typography>
                          )}
                        </LeaseHeader>

                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          {/* Lease Period */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="600" display="block" mb={1}>
                              Lease Period
                            </Typography>
                            <Stack spacing={0.75}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: 18 }} color="action" />
                                <Typography variant="body2">
                                  <strong>Start:</strong> {formatDate(lease.startDate)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: 18 }} color="action" />
                                <Typography variant="body2">
                                  <strong>End:</strong> {formatDate(lease.endDate)}
                                </Typography>
                              </Box>
                              {daysRemaining > 0 && (
                                <Chip
                                  label={`${daysRemaining} days remaining`}
                                  size="small"
                                  color={daysRemaining < 30 ? 'warning' : 'info'}
                                  variant="outlined"
                                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                />
                              )}
                            </Stack>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          {/* Lease Details */}
                          <Box sx={{ mb: 2 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Paper sx={{ bgcolor: 'grey.50', p: 1.5, textAlign: 'center' }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Type
                                  </Typography>
                                  <Typography variant="body2" fontWeight="600">
                                    {lease.leaseType.toUpperCase()}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6}>
                                <Paper sx={{ bgcolor: 'grey.50', p: 1.5, textAlign: 'center' }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Currency
                                  </Typography>
                                  <Typography variant="body2" fontWeight="600">
                                    {lease.currency}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          {/* Unit Information */}
                          <Box>
                            <Typography variant="subtitle2" color="primary" fontWeight="600" display="block" mb={1}>
                              Unit Details
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <HomeIcon sx={{ fontSize: 20 }} color="primary" />
                              <Typography variant="body1" fontWeight="600">
                                Unit {lease.unit.number}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • Floor {lease.unit.floor}
                              </Typography>
                            </Box>
                            <Grid container spacing={1}>
                              <Grid item xs={4}>
                                <Paper sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 1 }}>
                                  <MeetingRoomIcon sx={{ fontSize: 20 }} color="primary" />
                                  <Typography variant="body2" fontWeight="600" display="block">
                                    {lease.unit.rooms}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Rooms
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={4}>
                                <Paper sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 1 }}>
                                  <BathtubIcon sx={{ fontSize: 20 }} color="primary" />
                                  <Typography variant="body2" fontWeight="600" display="block">
                                    {lease.unit.bathrooms}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Baths
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={4}>
                                <Paper sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 1 }}>
                                  <SquareFootIcon sx={{ fontSize: 20 }} color="primary" />
                                  <Typography variant="body2" fontWeight="600" display="block">
                                    {lease.unit.areaM2}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    m²
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Fees Badge */}
                          {lease.fees && lease.fees.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Chip
                                icon={<AttachMoneyIcon />}
                                label={`${lease.fees.length} Fee${lease.fees.length !== 1 ? 's' : ''} Configured`}
                                color="success"
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          )}
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>

          {/* Installments Tab */}
          <TabPanel value={tabValue} index={1}>
            {installmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress size={60} />
              </Box>
            ) : installments.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                <ReceiptIcon sx={{ fontSize: 60, color: 'grey.300', mb: 1 }} />
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  No installments found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This tenant doesn't have any installments yet.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {Object.entries(groupedInstallments).map(([leaseId, { lease, installments: leaseInstallments }]) => (
                  <Box key={leaseId}>
                    {/* Lease Information Header */}
                    <Paper sx={{ p: 1.5, mb: 1, bgcolor: 'primary.main', color: 'white' }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" fontWeight="bold">
                            Lease: {lease.refNumber || `#${lease.id.substring(0, 8)}`}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(lease.startDate)} - {formatDate(lease.endDate)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                          <Chip
                            label={getStatusInfo(lease.status).label}
                            color={getStatusInfo(lease.status).color}
                            sx={{ fontWeight: 'bold', mb: 1 }}
                          />
                          <Typography variant="body2">
                            {leaseInstallments.length} Installment{leaseInstallments.length !== 1 ? 's' : ''}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Installments Table */}
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell><strong>Installment ID</strong></TableCell>
                            <TableCell><strong>Issued Date</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {leaseInstallments.map((installment) => {
                            const statusInfo = getInstallmentStatusInfo(installment.status);
                            return (
                              <TableRow key={installment.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="500">
                                    #{installment.id.substring(0, 8)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                      {formatDate(installment.issuedAt)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                      {formatDate(installment.dueDate)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                    <Typography variant="body2" fontWeight="600" color="success.main">
                                      {installment.total.toLocaleString()}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={statusInfo.label}
                                    color={statusInfo.color}
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Total Row */}
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell colSpan={3}>
                              <Typography variant="body1" fontWeight="bold">
                                Total for this Lease
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight="bold" color="success.main">
                                {leaseInstallments.reduce((sum, inst) => sum + inst.total, 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Stack>
            )}
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};

export default TenantDetailsPage;
